import React from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { FormattedCurrencyV2, SummaryGridItem, SummarySection, formatTermPaymentName } from '@css-xp/base';
import { TaxComponent } from '@css-xp/base/tax-exempt/src/tax-component';
import './term-details-summary.scss';
import Tooltip from '@atlaskit/tooltip';
import { TAX_LABEL } from '@atlassian/cssxp-types-billing';
import { TermDetailsAccount, TransactionAccount } from '@css-xp/graphtypes/src/generated-types-new';

interface TermDetailsSummaryProps {
  transactionAccount: TransactionAccount | undefined;
  currentAccount: TermDetailsAccount;
}

const TermDetailsSummary: React.FC<TermDetailsSummaryProps> = ({ transactionAccount, currentAccount }) => {
  const gstTaxId = transactionAccount?.billToParty?.taxIds?.filter((taxDetails) => taxDetails?.label === TAX_LABEL.GST); // Needs to be removed and done with help of TAxExmept component.
  const billToAddress = { ...transactionAccount?.billToParty, taxIds: gstTaxId };
  const transactionAccountNumber = transactionAccount?.transactionAccountNumber;
  const taxExemptFlag = billToAddress?.taxExempt ? 'Yes' : 'No';
  const totalCreditLimit = transactionAccount?.billingAccountsList?.totalCreditLimit;
  const availableCreditLimit = transactionAccount?.billingAccountsList?.availableCreditLimit;
  const creditOutstanding = transactionAccount?.billingAccountsList?.creditOutstanding;
  const currency = transactionAccount?.billingAccountsList?.currency;
  const termType = transactionAccount?.billingAccountsList?.paymentMethod;
  const totalTaxIds = transactionAccount?.billToParty?.taxIds?.length || 0;

  return (
    <SummarySection columns="1fr 2fr" className="term-details-summary">
      <SummaryGridItem className="summary-grid-item-internal">
        <SummarySection className="summary-section-internal" columns={totalTaxIds > 1 ? '1fr' : '1fr 1fr'}>
          <TaxComponent
            billToAddress={billToAddress}
            transactionAccountNumber={transactionAccountNumber}
            taxExemptFlag={taxExemptFlag}
          />
        </SummarySection>
      </SummaryGridItem>

      <SummaryGridItem className="summary-grid-item-internal">
        <SummarySection
          className="summary-section-internal"
          columns={termType === 'NET30' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'}
        >
          <SummaryGridItem className="section-item-internal">
            <>
              <Tooltip
                position="top"
                content={`Total credit limit for the ${formatTermPaymentName(termType)} payment terms`}
              >
                <label>
                  Total credit limit{' '}
                  <span>
                    <InfoIcon size="small" label="" />
                  </span>
                </label>
              </Tooltip>
              {totalCreditLimit !== null ? (
                <div className="term-detail-currency">
                  <FormattedCurrencyV2 value={totalCreditLimit} currency={currency} showCurrencyCode showCommas />
                </div>
              ) : (
                <div className="term-detail-currency">N/A</div>
              )}
            </>
          </SummaryGridItem>
          {termType === 'NET30' && (
            <SummaryGridItem className="section-item-internal">
              <>
                <Tooltip
                  position="top"
                  content={`Total credit utilised across all billing accounts for Net-30 payment terms`}
                >
                  <label>
                    Total credit limit consumed{' '}
                    <span>
                      <InfoIcon size="small" label="" />
                    </span>
                  </label>
                </Tooltip>
                {creditOutstanding !== null ? (
                  <div className="term-detail-currency">
                    <FormattedCurrencyV2 value={creditOutstanding} currency={currency} showCurrencyCode showCommas />
                  </div>
                ) : (
                  <div className="term-detail-currency">N/A</div>
                )}
              </>
            </SummaryGridItem>
          )}
          <SummaryGridItem className="section-item-internal">
            <>
              <Tooltip
                position="top"
                content={`Available credit limit for ${formatTermPaymentName(termType)} payment terms`}
              >
                <label>
                  Total available limit{' '}
                  <span>
                    <InfoIcon size="small" label="" />
                  </span>
                </label>
              </Tooltip>
              {availableCreditLimit !== null ? (
                <div className="term-detail-currency">
                  <FormattedCurrencyV2 currency={currency} value={availableCreditLimit} showCurrencyCode showCommas />
                </div>
              ) : (
                <div className="term-detail-currency">N/A</div>
              )}
            </>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <>
              <label>Credit utilised by this billing account </label>
              {currentAccount?.creditOutstanding !== null ? (
                <div className="term-detail-currency">
                  <FormattedCurrencyV2
                    value={currentAccount?.creditOutstanding}
                    currency={currency}
                    showCurrencyCode
                    showCommas
                  />
                </div>
              ) : (
                <div className="term-detail-currency">N/A</div>
              )}
            </>
          </SummaryGridItem>
        </SummarySection>
      </SummaryGridItem>
    </SummarySection>
  );
};

export default TermDetailsSummary;
